import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  InputBase,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useMemo, useState } from "react";
import { BentoCategory } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

import { Icon } from "../../Gamification/components/shared/icons";
import styles from "./styles";

interface Props {
  open: boolean;
  onClose: () => void;
}

const OTHER_LABEL = "Other";

const EditPitchFiltersDrawer = ({ open, onClose }: Props) => {
  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const { topLevelCategories } = useContext(BentoCategoriesContext);

  const [draft, setDraft] = useState<BentoCategory[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const selected = currentOrg?.bentoCategories ?? [];
      setDraft(makeDeepCopy(selected));
      setSearch("");
      const selectedIds = new Set(selected.map((category) => category.id));
      const initialExpanded: Record<number, boolean> = {};
      for (const category of topLevelCategories) {
        const ids = [
          category.id,
          ...(category.subcategories ?? []).map((x) => x.id),
        ];
        if (ids.some((id) => selectedIds.has(id))) {
          initialExpanded[category.id] = true;
        }
      }
      setExpanded(initialExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const draftIds = useMemo(
    () => new Set(draft.map((category) => category.id)),
    [draft],
  );

  const chipsForCategory = (category: BentoCategory) => [
    ...(category.subcategories ?? []),
    { ...category, name: OTHER_LABEL },
  ];

  const selectedCount = (category: BentoCategory) =>
    chipsForCategory(category).filter((chip) => draftIds.has(chip.id)).length;

  const categoryState = (category: BentoCategory) => {
    const count = selectedCount(category);
    if (count === 0) return "none" as const;
    if (count === chipsForCategory(category).length) return "all" as const;
    return "some" as const;
  };

  const toggleChip = (chip: BentoCategory) => {
    setDraft((prev) => {
      const copy = makeDeepCopy(prev);
      const index = copy.findIndex((x: BentoCategory) => x.id === chip.id);
      if (index > -1) {
        copy.splice(index, 1);
      } else {
        copy.push(chip);
      }
      return copy;
    });
  };

  const toggleSelectAll = (category: BentoCategory) => {
    const chips = chipsForCategory(category);
    const allSelected = categoryState(category) === "all";
    setDraft((prev) => {
      const copy = makeDeepCopy(prev);
      const chipIds = chips.map((x) => x.id);
      const remaining = copy.filter(
        (x: BentoCategory) => !chipIds.includes(x.id),
      );
      if (allSelected) {
        return remaining;
      }
      return [...remaining, ...chips];
    });
  };

  const toggleExpand = (categoryId: number) => {
    setExpanded((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const visibleCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return topLevelCategories;
    return topLevelCategories.filter((category) => {
      if (category.name.toLowerCase().includes(query)) return true;
      return (category.subcategories ?? []).some((sub) =>
        sub.name.toLowerCase().includes(query),
      );
    });
  }, [search, topLevelCategories]);

  const isExpanded = (category: BentoCategory) =>
    Boolean(search.trim()) || Boolean(expanded[category.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { categories } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/categories`,
        "PUT",
        {},
        { categoryIds: draft.map((category) => category.id) },
      );
      setCurrentOrg((prev) => {
        const copy = makeDeepCopy(prev);
        copy.bentoCategories = categories;
        return copy;
      });
      setAlert("Pitch filters updated", "success");
      onClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: styles.drawerPaper }}
    >
      <Box sx={styles.header}>
        <Box sx={styles.headerIcon}>
          <Icon.Search size={20} />
        </Box>
        <Box sx={styles.headerText}>
          <Typography sx={styles.headerTitle}>Edit pitch filters</Typography>
          <Typography sx={styles.headerSubtitle}>
            We use these to surface new brands on your home page.
          </Typography>
        </Box>
        <IconButton sx={styles.closeButton} onClick={onClose}>
          <Icon.X size={18} />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        <Box sx={styles.sectionHead}>
          <Typography sx={styles.sectionTitle}>Niches</Typography>
        </Box>
        <Typography sx={styles.sectionSubtitle}>
          {draft.length} selected · brands tagged in any of these will surface
        </Typography>

        <Box sx={styles.searchBox}>
          <Icon.Search size={18} />
          <InputBase
            sx={styles.searchInput}
            placeholder="Search niches..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <IconButton sx={styles.searchClear} onClick={() => setSearch("")}>
              <Icon.X size={15} />
            </IconButton>
          )}
        </Box>

        {visibleCategories.length === 0 ? (
          <Typography sx={styles.emptyState}>No niches found.</Typography>
        ) : (
          visibleCategories.map((category) => {
            const state = categoryState(category);
            const expandedNow = isExpanded(category);
            return (
              <Box
                key={category.id}
                sx={
                  state === "none"
                    ? styles.categoryCard
                    : styles.categoryCardActive
                }
              >
                <Box
                  sx={styles.categoryRow}
                  onClick={() => toggleExpand(category.id)}
                >
                  <Box
                    sx={styles.checkbox(state)}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelectAll(category);
                    }}
                  >
                    {state === "all" && <Icon.Check size={14} />}
                    {state === "some" && (
                      <Box
                        component="span"
                        sx={{ width: 10, height: 2, backgroundColor: "white" }}
                      />
                    )}
                  </Box>
                  <Typography sx={styles.categoryName}>
                    {category.name}
                  </Typography>
                  {state !== "none" && (
                    <Box component="span" sx={styles.countBadge}>
                      {selectedCount(category)} of{" "}
                      {chipsForCategory(category).length}
                    </Box>
                  )}
                  <Box sx={styles.chevron}>
                    <Icon.Chevron size={18} open={expandedNow} />
                  </Box>
                </Box>

                {expandedNow && (
                  <Box sx={styles.chipArea}>
                    {chipsForCategory(category).map((chip) => {
                      const selected = draftIds.has(chip.id);
                      return (
                        <Box
                          key={chip.id}
                          sx={styles.chip(selected)}
                          onClick={() => toggleChip(chip)}
                        >
                          {selected && <Icon.Check size={13} />}
                          {chip.name}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      <Box sx={styles.footer}>
        <Typography sx={styles.footerCount}>
          {draft.length} niche{draft.length === 1 ? "" : "s"} selected
        </Typography>
        <Box sx={styles.footerActions}>
          <Button sx={styles.cancelButton} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disableElevation
            sx={styles.saveButton}
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={14} sx={{ color: "white" }} />
              ) : (
                <Icon.Check size={15} />
              )
            }
          >
            Save filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EditPitchFiltersDrawer;
